import { ErrorDeApi, type Problema } from './problema';

const BASE = import.meta.env['VITE_API_BASE'] ?? '/api/v1';

let obtenerToken: () => string | null = () => null;
let renovarToken: (() => Promise<string | null>) | null = null;

export function configurarToken(proveedor: () => string | null): void {
  obtenerToken = proveedor;
}

export function configurarRenovacion(renovar: () => Promise<string | null>): void {
  renovarToken = renovar;
}

interface Opciones extends Omit<RequestInit, 'body'> {
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
}

let renovacionEnCurso: Promise<string | null> | null = null;

async function renovarUnaVez(): Promise<string | null> {
  if (!renovarToken) {
    return null;
  }
  renovacionEnCurso ??= renovarToken().finally(() => {
    renovacionEnCurso = null;
  });
  return renovacionEnCurso;
}

async function solicitar<T>(ruta: string, opciones: Opciones = {}, reintentando = false): Promise<T> {
  const { body, query, headers, ...resto } = opciones;

  const url = new URL(`${BASE}${ruta}`, window.location.origin);
  for (const [clave, valor] of Object.entries(query ?? {})) {
    if (valor !== undefined) {
      url.searchParams.set(clave, String(valor));
    }
  }

  const token = obtenerToken();

  const esFormulario = body instanceof FormData;

  const respuesta = await fetch(url, {
    ...resto,
    headers: {
      Accept: 'application/json',
      ...(body !== undefined && !esFormulario ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers,
    },
    ...(body !== undefined ? { body: esFormulario ? body : JSON.stringify(body) } : {}),
  });

  if (respuesta.status === 401 && !reintentando && renovarToken) {
    const nuevo = await renovarUnaVez();
    if (nuevo) {
      return solicitar<T>(ruta, opciones, true);
    }
  }

  if (!respuesta.ok) {
    throw new ErrorDeApi(await leerProblema(respuesta));
  }

  if (respuesta.status === 204 || respuesta.headers.get('content-length') === '0') {
    return undefined as T;
  }

  const texto = await respuesta.text();
  return (texto ? JSON.parse(texto) : undefined) as T;
}

async function leerProblema(respuesta: Response): Promise<Problema> {
  try {
    const cuerpo = (await respuesta.json()) as Partial<Problema>;
    return {
      ...cuerpo,
      status: cuerpo.status ?? respuesta.status,
      codigo: cuerpo.codigo ?? `http.${respuesta.status}`,
    };
  } catch {
    return {
      status: respuesta.status,
      codigo: `http.${respuesta.status}`,
      detail: respuesta.statusText || 'NO SE PUDO CONTACTAR CON EL SERVIDOR',
    };
  }
}

export const api = {
  get: <T>(ruta: string, opciones?: Opciones) => solicitar<T>(ruta, { ...opciones, method: 'GET' }),

  subir: <T>(ruta: string, formulario: FormData) =>
    solicitar<T>(ruta, { method: 'POST', body: formulario }),

  descargar: async (ruta: string): Promise<Blob | null> => {
    const url = new URL(`${BASE}${ruta}`, window.location.origin);
    const token = obtenerToken();

    const respuesta = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    if (respuesta.status === 404) {
      return null;
    }
    if (!respuesta.ok) {
      throw new ErrorDeApi(await leerProblema(respuesta));
    }
    return respuesta.blob();
  },

  post: <T>(ruta: string, body?: unknown, opciones?: Opciones) =>
    solicitar<T>(ruta, { ...opciones, method: 'POST', body }),
  put: <T>(ruta: string, body?: unknown, opciones?: Opciones) =>
    solicitar<T>(ruta, { ...opciones, method: 'PUT', body }),
  patch: <T>(ruta: string, body?: unknown, opciones?: Opciones) =>
    solicitar<T>(ruta, { ...opciones, method: 'PATCH', body }),
  delete: <T>(ruta: string, opciones?: Opciones) =>
    solicitar<T>(ruta, { ...opciones, method: 'DELETE' }),
};

export interface Pagina<T> {
  contenido: T[];
  pagina: number;
  tamanio: number;
  totalElementos: number;
  totalPaginas: number;
}

export interface PaginaDeCorrecciones<T> extends Pagina<T> {
  pendientes: number;
  historial: number;
}
